import type { Patient, Reference } from '@medplum/fhirtypes'
import { ICD10_SYSTEM, SEEN_HEALTH_SYSTEM } from '@seen/core/codeSystems'
import { invariant } from '@seen/core/invariant'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import type { OrganizationConditionsList } from '@seen/core/models/organizationConditionsList'
import { getReferenceString } from '@seen/core/references'
import type { Icd10Suggestion } from '@seen/request-types/suggestIcd10'
import { Button, cn } from '@seen/ui'
import { IconPlus, IconRefresh, IconSparkles, IconX } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { type ExternalToast, toast } from 'sonner'
import {
  applyConditionReplacement,
  recordConditionOnOrgList,
} from '#src/components/condition-picker/replaceCondition.ts'
import type { ConditionPickerCondition } from '#src/components/condition-picker/types.ts'
import {
  ConditionCreateConfirmDialog,
  type ConditionCreateConfirmValues,
} from '#src/components/ConditionCreateConfirmDialog.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIcd10Search } from '#src/hooks/useIcd10Search.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { findCreatedDiagnosis } from '#src/utils/findCreatedDiagnosis.ts'

interface CreateCustomConditionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialName: string
  patientRef: Reference<Patient>
  orgConditionsList: OrganizationConditionsList | undefined
  onConditionCreated: (condition: ConditionPickerCondition) => void
  toastPosition?: ExternalToast['position']
  // When set, the authored custom condition replaces this existing diagnosis in
  // place (preserving onset and clinical status) instead of creating a new one.
  replaceTarget?: Diagnosis
}

interface FormErrors {
  conditionName?: string
  selectedCodes?: string
}

const EMPTY_SET = new Set<string>()

// Title for the org-wide reusable template, which every practitioner who later
// searches these codes will see. A single code contributes its official
// ICD-10-CM display so the shared label can never contradict the code it is
// filed under; the author's free text stays on their participant's own
// condition. Multi-code entries keep the typed name because no single official
// display describes a combination of codes.
function orgTemplateTitle(
  conditionName: string,
  selectedCodes: Icd10Suggestion[]
): string {
  const [onlyCode, ...rest] = selectedCodes
  return onlyCode && rest.length === 0 ? onlyCode.display : conditionName
}

export function CreateCustomConditionDialog({
  open,
  onOpenChange,
  initialName,
  patientRef,
  orgConditionsList,
  onConditionCreated,
  toastPosition,
  replaceTarget,
}: CreateCustomConditionDialogProps): JSX.Element {
  const isReplace = replaceTarget !== undefined
  const medplum = useMedplum()
  const transactionMutation = useTransactionMutation()
  const { mutateAsync: mutateTransaction } = transactionMutation

  const [conditionName, setConditionName] = useState(initialName)
  const [selectedCodes, setSelectedCodes] = useState<Icd10Suggestion[]>([])
  const [icd10Search, setIcd10Search] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [confirmOpen, setConfirmOpen] = useState(false)

  const conditionNameRef = useRef<HTMLInputElement>(null)
  const icd10SearchRef = useRef<HTMLInputElement>(null)

  const toastOptions = useMemo<ExternalToast | undefined>(
    () => (toastPosition ? { position: toastPosition } : undefined),
    [toastPosition]
  )

  const { ai } = useSeenApi()
  const suggestionsMutation = useMutation({
    mutationKey: ['seen-api', 'suggestIcd10'] as const,
    // Both call sites (the on-open effect and the Regenerate button) only run
    // when `ai` is wired, so this is an unreachable-precondition assertion,
    // not a runtime branch.
    mutationFn: (name: string) => {
      invariant(ai, 'ICD-10 suggestions are unavailable')
      return ai.suggestIcd10({ conditionName: name })
    },
  })

  const suggestions = suggestionsMutation.data?.suggestions ?? []

  const selectedCodeSet = useMemo(
    () => new Set(selectedCodes.map((c) => c.code)),
    [selectedCodes]
  )

  const { results: icd10Results, isLoading: isIcd10Loading } = useIcd10Search({
    query: icd10Search,
    enabled: icd10Search.trim().length > 0,
    existingParticipantIcd10Codes: EMPTY_SET,
    existingOrganizationConditionTitles: EMPTY_SET,
  })

  const { mutate: suggestIcd10, reset: resetSuggestions } = suggestionsMutation

  useEffect(() => {
    if (open && initialName.trim()) {
      setConditionName(initialName)
      setSelectedCodes([])
      setIcd10Search('')
      setErrors({})
      setConfirmOpen(false)
      resetSuggestions()
      if (ai) {
        suggestIcd10(initialName.trim())
      }
    }
  }, [open, initialName, suggestIcd10, resetSuggestions, ai])

  const addCode = useCallback(
    (suggestion: Icd10Suggestion) => {
      setSelectedCodes((prev) => {
        if (prev.some((c) => c.code === suggestion.code)) {
          return prev
        }
        return [...prev, suggestion]
      })
      if (errors.selectedCodes) {
        setErrors((prev) => ({ ...prev, selectedCodes: undefined }))
      }
    },
    [errors.selectedCodes]
  )

  const removeCode = useCallback((code: string) => {
    setSelectedCodes((prev) => prev.filter((c) => c.code !== code))
  }, [])

  const validate = useCallback((): FormErrors => {
    const newErrors: FormErrors = {}
    if (!conditionName.trim()) {
      newErrors.conditionName = 'Enter a condition name'
    }
    if (selectedCodes.length === 0) {
      newErrors.selectedCodes = 'Add at least one ICD-10 code'
    }
    return newErrors
  }, [conditionName, selectedCodes])

  const handleReplace = useCallback(async (): Promise<void> => {
    if (!replaceTarget) {
      return
    }
    const name = conditionName.trim()
    // Apply to a clone, not the cached target, so a failed transaction can't
    // leave the in-memory model ahead of the server.
    const updated = Diagnosis.fromFhirResource(
      structuredClone(replaceTarget.toFhirResource())
    )
    applyConditionReplacement(updated, {
      title: name,
      icd10: selectedCodes.map((c) => ({
        code: c.code,
        display: c.display,
      })),
      custom: true,
    })
    try {
      await mutateTransaction((builder) => {
        builder.update(updated.toFhirResource())
        if (orgConditionsList) {
          recordConditionOnOrgList(
            orgConditionsList,
            {
              title: orgTemplateTitle(name, selectedCodes),
              icd10Codes: selectedCodes.map((c) => ({
                system: ICD10_SYSTEM,
                code: c.code,
                display: c.display,
              })),
              source: 'custom',
            },
            medplum
          )
          builder.update(orgConditionsList.toFhirResource())
        }
      })
      toast.success(`Replaced with ${name}`, toastOptions)
      onOpenChange(false)
      onConditionCreated({
        id: `custom-${Date.now()}`,
        title: name,
        icd10Code: selectedCodes.map((c) => c.code).join(', '),
        section: 'organization',
        diagnosis: updated,
      })
    } catch {
      toast.error(`Failed to replace condition: ${name}`, toastOptions)
    }
  }, [
    replaceTarget,
    conditionName,
    selectedCodes,
    orgConditionsList,
    medplum,
    mutateTransaction,
    onOpenChange,
    onConditionCreated,
    toastOptions,
  ])

  const handleContinue = useCallback(() => {
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      if (validationErrors.conditionName) {
        conditionNameRef.current?.focus()
      } else if (validationErrors.selectedCodes) {
        icd10SearchRef.current?.focus()
      }
      return
    }
    if (isReplace) {
      void handleReplace()
      return
    }
    setConfirmOpen(true)
  }, [validate, isReplace, handleReplace])

  const handleConfirmCreate = useCallback(
    async (values: ConditionCreateConfirmValues): Promise<void> => {
      const name = conditionName.trim()
      const diagnosis = new Diagnosis({
        subject: patientRef,
        title: name,
        clinicalStatus: values.clinicalStatus,
      })
      diagnosis.onsetDate = values.onsetDate
      diagnosis.addTag({ system: SEEN_HEALTH_SYSTEM, code: 'custom-condition' })
      diagnosis.code = {
        text: name,
        coding: selectedCodes.map((c) => ({
          system: ICD10_SYSTEM,
          code: c.code,
          display: c.display,
        })),
      }

      try {
        const bundle = await mutateTransaction((builder) => {
          builder.create(diagnosis)
          if (orgConditionsList) {
            const profile = medplum.getProfile()
            orgConditionsList.add({
              title: orgTemplateTitle(name, selectedCodes),
              icd10Codes: selectedCodes.map((c) => ({
                system: ICD10_SYSTEM,
                code: c.code,
                display: c.display,
              })),
              snomedCodes: [],
              addedOn: new Date().toISOString(),
              addedBy: profile
                ? { reference: getReferenceString(profile) }
                : { reference: 'Organization/seen-health' },
              source: 'custom',
            })
            builder.update(orgConditionsList.toFhirResource())
          }
        })

        const created = findCreatedDiagnosis(bundle)
        if (!created) {
          toast.error(`Failed to create condition: ${name}`, toastOptions)
          return
        }
        toast.success(`Created condition: ${name}`, toastOptions)
        setConfirmOpen(false)
        onOpenChange(false)
        onConditionCreated({
          id: `custom-${Date.now()}`,
          title: name,
          icd10Code: selectedCodes.map((c) => c.code).join(', '),
          section: 'organization',
          diagnosis: created,
        })
      } catch {
        toast.error(`Failed to create condition: ${name}`, toastOptions)
      }
    },
    [
      conditionName,
      selectedCodes,
      patientRef,
      orgConditionsList,
      medplum,
      mutateTransaction,
      onOpenChange,
      onConditionCreated,
      toastOptions,
    ]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay notScrollable>
        <div className="flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {isReplace
                ? 'Replace with Custom Condition'
                : 'Create Custom Condition'}
            </DialogTitle>
          </DialogHeader>
          <DialogContent className="space-y-4 min-h-0 flex-1 overflow-y-auto">
            <div>
              <label className="mb-1.5 block text-sm font-medium">
                Condition Name
              </label>
              <Input
                ref={conditionNameRef}
                value={conditionName}
                onChange={(e) => {
                  setConditionName(e.target.value)
                  if (errors.conditionName) {
                    setErrors((prev) => ({
                      ...prev,
                      conditionName: undefined,
                    }))
                  }
                }}
                placeholder="Enter condition name…"
                autoFocus
                aria-invalid={!!errors.conditionName}
              />
              {errors.conditionName && (
                <p className="mt-1.5 text-sm text-destructive">
                  {errors.conditionName}
                </p>
              )}
            </div>

            <div className="border-t pt-4">
              <div className="mb-2 flex items-center justify-between">
                <label className="text-sm font-medium">ICD-10 Codes</label>
                {ai && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      conditionName.trim() && suggestIcd10(conditionName.trim())
                    }
                    disabled={
                      !conditionName.trim() || suggestionsMutation.isPending
                    }
                  >
                    <IconRefresh className="size-3.5" />
                    Regenerate
                  </Button>
                )}
              </div>

              {selectedCodes.length > 0 && (
                <div className="space-y-1 mb-3">
                  {selectedCodes.map((code) => (
                    <div
                      key={code.code}
                      className="flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm"
                    >
                      <span className="shrink-0 font-mono font-semibold">
                        {code.code}
                      </span>
                      <span className="truncate text-muted-foreground">
                        {code.display}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeCode(code.code)}
                        className="ml-auto shrink-0 text-muted-foreground hover:text-foreground"
                      >
                        <IconX className="size-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {errors.selectedCodes && selectedCodes.length === 0 && (
                <p className="mb-3 text-sm text-destructive">
                  {errors.selectedCodes}
                </p>
              )}

              {suggestionsMutation.isPending ? (
                <div className="flex items-center justify-center py-4">
                  <Loader />
                </div>
              ) : suggestions.length > 0 ? (
                <div className="space-y-1 mb-3">
                  <div className="mb-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <IconSparkles className="size-3" />
                    <span>AI Suggestions</span>
                  </div>
                  {suggestions.map((suggestion) => {
                    const isSelected = selectedCodeSet.has(suggestion.code)
                    return (
                      <button
                        key={suggestion.code}
                        type="button"
                        onClick={() =>
                          addCode({
                            code: suggestion.code,
                            display: suggestion.display,
                          })
                        }
                        disabled={isSelected}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm transition-colors',
                          isSelected
                            ? 'cursor-default opacity-40'
                            : 'cursor-pointer hover:bg-accent'
                        )}
                      >
                        <IconPlus
                          className={cn(
                            'size-3.5 shrink-0',
                            isSelected && 'invisible'
                          )}
                        />
                        <span className="shrink-0 font-mono font-semibold">
                          {suggestion.code}
                        </span>
                        <span className="truncate text-muted-foreground">
                          {suggestion.display}
                        </span>
                      </button>
                    )
                  })}
                </div>
              ) : suggestionsMutation.isError ? (
                <p className="py-2 text-sm text-destructive">
                  Failed to load suggestions. Try regenerating.
                </p>
              ) : suggestionsMutation.isSuccess && suggestions.length === 0 ? (
                <p className="py-2 text-sm text-muted-foreground">
                  No suggestions found. Try searching manually below.
                </p>
              ) : (
                suggestionsMutation.isIdle && (
                  <p className="py-2 text-sm text-muted-foreground">
                    Enter a condition name and suggestions will appear.
                  </p>
                )
              )}

              <div className="mt-3 border-t pt-3">
                <label className="mb-1.5 block text-xs text-muted-foreground">
                  Search ICD-10 codes
                </label>
                <Input
                  ref={icd10SearchRef}
                  value={icd10Search}
                  onChange={(e) => setIcd10Search(e.target.value)}
                  placeholder="Search by name or code…"
                />
                {icd10Search.trim() && (
                  <div className="mt-1 max-h-[160px] overflow-y-auto rounded-md border">
                    {isIcd10Loading ? (
                      <div className="flex justify-center py-3">
                        <Loader />
                      </div>
                    ) : icd10Results.length > 0 ? (
                      icd10Results.slice(0, 20).map((result) => {
                        const isSelected = selectedCodeSet.has(result.code)
                        return (
                          <button
                            key={result.code}
                            type="button"
                            onClick={() =>
                              addCode({
                                code: result.code,
                                display: result.name,
                              })
                            }
                            disabled={isSelected}
                            className={cn(
                              'flex w-full items-center gap-2 border-b px-3 py-1.5 text-left text-sm transition-colors last:border-b-0',
                              isSelected
                                ? 'cursor-default opacity-40'
                                : 'cursor-pointer hover:bg-accent'
                            )}
                          >
                            <IconPlus
                              className={cn(
                                'size-3.5 shrink-0',
                                isSelected && 'invisible'
                              )}
                            />
                            <span className="shrink-0 font-mono font-semibold">
                              {result.code}
                            </span>
                            <span className="truncate text-muted-foreground">
                              {result.name}
                            </span>
                          </button>
                        )
                      })
                    ) : (
                      <p className="px-3 py-2 text-sm text-muted-foreground">
                        No results found.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
          <DialogFooter className="sticky bottom-0 gap-2 border-t bg-background">
            <DialogClose asChild>
              <Button
                variant="outline"
                disabled={transactionMutation.isPending}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleContinue}
              loading={isReplace && transactionMutation.isPending}
              disabled={transactionMutation.isPending}
            >
              {isReplace ? 'Replace' : 'Continue'}
            </Button>
          </DialogFooter>
        </div>
      </DialogDisplay>
      <ConditionCreateConfirmDialog
        open={confirmOpen}
        onOpenChange={(next) => {
          if (!transactionMutation.isPending) {
            setConfirmOpen(next)
          }
        }}
        conditionTitle={conditionName.trim()}
        onConfirm={handleConfirmCreate}
        isPending={transactionMutation.isPending}
      />
    </Dialog>
  )
}
